import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Topic } from '../../../topic-player/interfaces/topic.interface';
import { BrowseTopicsCms } from '../../interfaces/browse-topics-cms.interface';
import { lmwBaseRoutes } from '../../routing/route-locales/lmw-base-routes';
import { lmwAuthLazyLoadedState } from '../../../auth-process/common/routing/route-locales/app-routes';
import { signInLazyLoaded } from '../../../auth-process/sign-in/routing/route-locales/sign-in-routes';
import { signInUsernameState } from '../../../auth-process/sign-in/routing/sign-in.states';
import { registrationLazyLoaded } from '../../../auth-process/registration/routing/route-locales/registration-routes';
import { TopicService } from '../../../topic-player/services/topic.service';
import { TranslateService } from '@nx/mono-common';
import { environment } from 'apps/learn-my-way/src/environments/environment';
import { Subject } from '../../../topic-player/interfaces/subject.interface';

@Component({
  selector: 'lmw-base-browse-topics',
  templateUrl: './browse-topics.component.html',
  styleUrls: ['./browse-topics.component.scss'],
})
export class BrowseTopicsComponent implements OnInit {
  public topics: (Topic & { completedDate?: string })[];
  public userSignedIn: boolean;
  public subjectName: string;
  public browseTopicsCms: BrowseTopicsCms;
  public browseSubjectsPageTitle: string = lmwBaseRoutes.browseSubjectsPageTitle;
  public signInUsernamePath = `/${lmwAuthLazyLoadedState.path}/${signInLazyLoaded.path}/${signInUsernameState.path}`;
  public registrationPath = `/${lmwAuthLazyLoadedState.path}/${registrationLazyLoaded.path}`;
  public topicCompletedDate: string;
  public subject: Subject;

  private completedTopicSlugs = [];
  private localeId: string;

  constructor(
    private topicService: TopicService,
    private route: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.localeId = this.route.snapshot.data.localeId;
    this.topics = this.route.snapshot.data.topics;
    this.userSignedIn = this.route.snapshot.data.userSignedIn;
    this.subject = this.route.snapshot.data.subject;
    this.browseTopicsCms = this.route.snapshot.data.cmsData;
    if (this.userSignedIn) {
      this.topics.forEach((topic) => {
        if (topic.completed) {
          this.completedTopicSlugs.push(topic.sk);
        }
      });
      this.getAllCompletedTopics();
    }
  }

  public printSummary() {
    window.print();
  }

  public translate(template: string) {
    return this.translateService.translate(template, environment.language);
  }

  private getAllCompletedTopics() {
    this.topicService
      .getTopicsAttemptsByCompleted(true)
      .subscribe((topicsFound) => {
        if (topicsFound) {
          this.filterAllCompletedTopicsByCompletedTopicsSlugs(topicsFound);
        }
      });
  }

  private filterAllCompletedTopicsByCompletedTopicsSlugs(
    allCompletedTopicsFound
  ) {
    const topicsCompletedInThisSubject: any[] = allCompletedTopicsFound.filter((topicAttempt) =>
      this.completedTopicSlugs.includes(topicAttempt.SK.split(':')[1])
    );
    this.reduceFilteredTopicsByLatestDate(topicsCompletedInThisSubject)
  }

  private reduceFilteredTopicsByLatestDate(topicsCompletedInThisSubject) {
    const latestCompletedTopics: any[] = Object.values(
      topicsCompletedInThisSubject.reduce((latestDate, { SK, dateUpdated }) => {
        const topicSlugFromSK = SK.split(':')[1];
        if (latestDate[topicSlugFromSK]) {
          if (latestDate[topicSlugFromSK].dateUpdated < dateUpdated) {
            latestDate[topicSlugFromSK] = { topicSlugFromSK, dateUpdated };
          }
        } else {
          latestDate[topicSlugFromSK] = { topicSlugFromSK, dateUpdated };
        }
        return latestDate;
      }, {})
    );
    this.setTopicLastCompletedDates(latestCompletedTopics)
  }

  private setTopicLastCompletedDates(latestCompletedTopics) {
    this.topics.forEach((topic, index) => {
      const matchedTopic = latestCompletedTopics.find(
        (completedTopic) => completedTopic.topicSlugFromSK === topic.sk
      );
      if (matchedTopic) {
        this.topics[index].completedDate = this.formatDateForLocale(
          matchedTopic.dateUpdated
        );
      }
    });
  }

  private formatDateForLocale(topicCompletedDate): string {
    return new Date(topicCompletedDate)
      .toLocaleString(this.localeId)
      .split(',')[0];
  }
}
